import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Loader2, Plus, Eye, SquarePen, Trash2, Handshake, Mail, Phone, Calendar, Link as LinkIcon, MapPin, Globe, X } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkspace } from '../../context/WorkspaceContext';
import { crmAPI } from '../../lib/crm';

const EMPTY_FORM = {
    company_name: '', pic_name: '', pic_title: '', pic_email: '',
    pic_phone: '', pic_linkedin: '', company_address: '', company_website: '',
};

export default function PartnershipsPage() {
    const { currentWorkspace } = useWorkspace();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [detail, setDetail] = useState(null);

    const load = useCallback(async () => {
        if (!currentWorkspace?.id) return;
        try {
            setLoading(true);
            const res = await crmAPI.listPartnerships(currentWorkspace.id);
            setRows(res.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal memuat Contact');
        } finally {
            setLoading(false);
        }
    }, [currentWorkspace?.id]);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setFormOpen(true); };
    const openEdit = (row) => {
        setEditing(row);
        setForm({ ...EMPTY_FORM, ...row });
        setFormOpen(true);
    };

    const save = async () => {
        if (!form.company_name?.trim()) return toast.error('Nama Perusahaan wajib diisi');
        try {
            setSaving(true);
            if (editing) {
                await crmAPI.updatePartnership(editing.id, form);
                toast.success('Contact diperbarui');
            } else {
                await crmAPI.createPartnership({ ...form, workspace_id: currentWorkspace.id });
                toast.success('Contact dibuat');
            }
            setFormOpen(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal menyimpan');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Hapus contact "${row.company_name}"?`)) return;
        try {
            await crmAPI.deletePartnership(row.id);
            toast.success('Dihapus');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal menghapus');
        }
    };

    const openDetail = async (row) => {
        try {
            const res = await crmAPI.getPartnership(row.id);
            setDetail(res.data);
        } catch (e) {
            toast.error('Gagal memuat detail');
        }
    };

    return (
        <Layout title="Contact" subtitle="Perusahaan yang sedang di-approach / penjajakan">
            <div className="flex justify-end mb-4">
                <Button onClick={openCreate} data-testid="partnership-create-btn">
                    <Plus className="w-4 h-4 mr-2" /> Tambah Contact
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
                </div>
            ) : rows.length === 0 ? (
                <div className="text-center py-20 text-brand-text-muted">
                    <Handshake className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p>Belum ada contact</p>
                </div>
            ) : (
                <div className="bg-white border border-brand-border rounded-lg overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Perusahaan</TableHead>
                                <TableHead>PIC</TableHead>
                                <TableHead>Position</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Phone</TableHead>
                                <TableHead>Last Contact</TableHead>
                                <TableHead>Next Contact</TableHead>
                                <TableHead className="text-right">Aksi</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.id} data-testid={`partnership-row-${r.id}`}>
                                    <TableCell className="font-medium">{r.company_name}</TableCell>
                                    <TableCell>{r.pic_name || '-'}</TableCell>
                                    <TableCell>{r.pic_title || '-'}</TableCell>
                                    <TableCell>{r.pic_email || '-'}</TableCell>
                                    <TableCell>{r.pic_phone || '-'}</TableCell>
                                    <TableCell>{r.last_contact || '-'}</TableCell>
                                    <TableCell>{r.next_contact || '-'}</TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-1">
                                            <Button variant="ghost" size="icon" onClick={() => openDetail(r)} data-testid={`partnership-detail-${r.id}`}>
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`partnership-edit-${r.id}`}>
                                                <SquarePen className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="icon" onClick={() => remove(r)} data-testid={`partnership-delete-${r.id}`}>
                                                <Trash2 className="w-4 h-4 text-red-600" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Create / Edit Dialog */}
            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent className="max-w-2xl" data-testid="partnership-form">
                    <DialogHeader>
                        <DialogTitle>{editing ? 'Edit Contact' : 'Tambah Contact'}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Nama Perusahaan *" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} testid="pf-company" />
                        <FormField label="Website Perusahaan" value={form.company_website} onChange={(v) => setForm({ ...form, company_website: v })} testid="pf-website" />
                        <FormField label="Nama PIC" value={form.pic_name} onChange={(v) => setForm({ ...form, pic_name: v })} testid="pf-picname" />
                        <FormField label="Title / Position PIC" value={form.pic_title} onChange={(v) => setForm({ ...form, pic_title: v })} testid="pf-pictitle" />
                        <FormField label="Email PIC" value={form.pic_email} onChange={(v) => setForm({ ...form, pic_email: v })} testid="pf-email" />
                        <FormField label="Phone PIC" value={form.pic_phone} onChange={(v) => setForm({ ...form, pic_phone: v })} testid="pf-phone" />
                        <FormField label="LinkedIn PIC" value={form.pic_linkedin} onChange={(v) => setForm({ ...form, pic_linkedin: v })} testid="pf-linkedin" className="col-span-2" />
                        <div className="col-span-2">
                            <Label>Alamat Perusahaan</Label>
                            <Textarea value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} rows={2} data-testid="pf-address" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setFormOpen(false)}>Batal</Button>
                        <Button onClick={save} disabled={saving} data-testid="partnership-save-btn">
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail Drawer */}
            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="partnership-detail">
                    {detail && <PartnershipDetail row={detail} onReload={async () => {
                        const res = await crmAPI.getPartnership(detail.id);
                        setDetail(res.data);
                        await load();
                    }} />}
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

function FormField({ label, value, onChange, testid, className = '' }) {
    return (
        <div className={className}>
            <Label>{label}</Label>
            <Input value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        </div>
    );
}

function PartnershipDetail({ row, onReload }) {
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [nextContact, setNextContact] = useState('');
    const [content, setContent] = useState('');
    const [saving, setSaving] = useState(false);

    const addContact = async () => {
        if (!content.trim()) return toast.error('Isi kontak wajib diisi');
        try {
            setSaving(true);
            const payload = { date, content, stage: 'partnership' };
            if (nextContact) payload.next_contact = nextContact;
            await crmAPI.addContact(row.id, payload);
            setContent('');
            setNextContact('');
            toast.success('Kontak ditambahkan');
            await onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gagal');
        } finally {
            setSaving(false);
        }
    };

    const removeContact = async (cid) => {
        if (!window.confirm('Hapus kontak ini?')) return;
        try {
            await crmAPI.deleteContact(cid);
            await onReload();
        } catch (e) { toast.error('Gagal'); }
    };

    return (
        <div>
            <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                    <Handshake className="w-5 h-5 text-brand-primary" /> {row.company_name}
                </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <InfoLine icon={Handshake} label="PIC" value={`${row.pic_name || '-'} — ${row.pic_title || ''}`} />
                    <InfoLine icon={Mail} label="Email" value={row.pic_email} />
                    <InfoLine icon={Phone} label="Phone" value={row.pic_phone} />
                    <InfoLine icon={LinkIcon} label="LinkedIn" value={row.pic_linkedin} href={row.pic_linkedin} />
                    <InfoLine icon={Globe} label="Website" value={row.company_website} href={row.company_website} />
                    <InfoLine icon={MapPin} label="Alamat" value={row.company_address} />
                    <InfoLine icon={Calendar} label="Last Contact" value={row.last_contact} />
                    <InfoLine icon={Calendar} label="Next Contact" value={row.next_contact} />
                </div>

                <div className="border-t pt-4">
                    <div className="text-sm font-semibold mb-2">Tambah Kontak Baru</div>
                    <div className="flex gap-2 items-end flex-wrap">
                        <div>
                            <Label>Tanggal</Label>
                            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="contact-date" />
                        </div>
                        <div>
                            <Label>Next Contact</Label>
                            <Input type="date" value={nextContact} onChange={(e) => setNextContact(e.target.value)} className="w-40" data-testid="contact-next-date" />
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <Label>Isi Kontak</Label>
                            <Input value={content} onChange={(e) => setContent(e.target.value)} placeholder="Contoh: Meeting intro, follow-up email..." data-testid="contact-content" />
                        </div>
                        <Button onClick={addContact} disabled={saving} data-testid="contact-add-btn">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        </Button>
                    </div>
                </div>

                <div className="border-t pt-4">
                    <div className="text-sm font-semibold mb-2">Riwayat Kontak</div>
                    {(row.partnership_contacts || []).length === 0 ? (
                        <div className="text-sm text-brand-text-muted">Belum ada kontak tercatat</div>
                    ) : (
                        <div className="space-y-2">
                            {row.partnership_contacts.map((c) => (
                                <div key={c.id} className="flex items-start gap-3 p-3 bg-brand-accent-soft/30 rounded-lg">
                                    <Calendar className="w-4 h-4 mt-0.5 text-brand-primary flex-shrink-0" />
                                    <div className="flex-1">
                                        <div className="text-xs text-brand-text-muted">{c.date}</div>
                                        <div className="text-sm">{c.content}</div>
                                        {c.next_contact && (
                                            <div className="text-xs text-brand-primary mt-1">Next Contact: {c.next_contact}</div>
                                        )}
                                    </div>
                                    <Button variant="ghost" size="icon" onClick={() => removeContact(c.id)}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function InfoLine({ icon: Icon, label, value, href }) {
    return (
        <div className="flex items-start gap-2">
            <Icon className="w-4 h-4 mt-0.5 text-brand-text-muted flex-shrink-0" />
            <div>
                <div className="text-xs text-brand-text-muted">{label}</div>
                {href && value ? (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline break-all">{value}</a>
                ) : (
                    <div className="break-words">{value || '-'}</div>
                )}
            </div>
        </div>
    );
}
